import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, AlertTriangle, ClipboardList, FileX, Users2, Building2, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { AdminShell } from '../../components/AdminShell';

interface Overview {
  pendingCompanies: number;
  pendingInvoices: number;
  pendingApprovals: number;
  disputedInvoices: number;
  totalUsers: number;
  totalWorkspaces: number;
  activeQueueJobs: number;
}

/**
 * Admin home: tiles of platform-wide counters. Each tile deep-links to
 * the corresponding management surface.
 */
export function AdminOverviewPage() {
  const { t, i18n: _i } = useTranslation();
  void _i;
  const overview = useQuery<Overview>({
    queryKey: ['admin-overview'],
    queryFn: async () => (await api.get('/admin/overview')).data,
    refetchInterval: 60_000,
  });

  const tiles = [
    { icon: ClipboardList, key: 'pendingCompanies', value: overview.data?.pendingCompanies ?? 0, to: '/admin/queue' },
    { icon: FileX,         key: 'pendingInvoices',  value: overview.data?.pendingInvoices  ?? 0 },
    { icon: ShieldCheck,   key: 'pendingApprovals', value: overview.data?.pendingApprovals ?? 0 },
    { icon: AlertTriangle, key: 'disputedInvoices', value: overview.data?.disputedInvoices ?? 0 },
    { icon: Users2,        key: 'totalUsers',       value: overview.data?.totalUsers       ?? 0, to: '/admin/users' },
    { icon: Building2,     key: 'totalWorkspaces',  value: overview.data?.totalWorkspaces  ?? 0, to: '/admin/workspaces' },
    { icon: Layers,        key: 'activeJobs',       value: overview.data?.activeQueueJobs  ?? 0, to: '/admin/jobs' },
  ] as const;

  return (
    <AdminShell>
      <div className="px-8 py-7 max-w-7xl w-full mx-auto">
        <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500">{t('admin.eyebrow')}</div>
        <h1 className="display text-3xl font-semibold tracking-tight mt-1">{t('admin.overviewTitle')}</h1>
        <p className="text-sm text-ink-600 dark:text-ink-300 mt-2 max-w-2xl">{t('admin.overviewSubtitle')}</p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            const body = (
              <Card className="p-4 flex items-center gap-3 h-full">
                <div className="h-9 w-9 rounded-lg bg-accent-50 text-accent-700 flex items-center justify-center shrink-0">
                  <Icon size={16} />
                </div>
                <div>
                  <div className="text-xs text-ink-500">{t(`admin.tiles.${tile.key}`)}</div>
                  <div className="num text-xl font-semibold">{tile.value}</div>
                </div>
              </Card>
            );
            return 'to' in tile && tile.to ? (
              <Link key={tile.key} to={tile.to}>{body}</Link>
            ) : (
              <div key={tile.key}>{body}</div>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}
